import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Ban, Lock, Plus, Printer, Unlock, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, today, useCan, useLoad, useSubmit } from "@/lib/api";
import { Empty, ErrorBox, PageHeading } from "@/components/common";
import CashEntryForm from "@/components/forms/CashEntryForm";
import { printReceipt } from "@/lib/receipt";

function CloseDay({ data, onDone }) {
  const can = useCan();
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const { busy, error, run } = useSubmit();
  const close = data.close?.status === "closed" ? data.close : null;
  const reopen = async () => {
    const reason = window.prompt("Why are you reopening this day? (kept in the audit trail)");
    if (!reason) return;
    try { await api.post("/cashbook/reopen", { date: data.day, reason }); toast.success("Day reopened"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (close) {
    return (
      <section className={close.difference === 0 ? "close-card matched" : "close-card mismatch"} data-testid="day-closed-card">
        <div className="close-head"><Lock size={18} /><div><b>Day closed by {close.closed_by_name}</b><small>{new Date(close.closed_at).toLocaleString("en-IN")}</small></div></div>
        <div className="close-figures"><span>Expected <b>{inr(close.expected)}</b></span><span>Counted <b data-testid="closed-counted">{inr(close.counted)}</b></span><span>Difference <b data-testid="closed-difference">{close.difference > 0 ? "+" : ""}{inr(close.difference)}</b></span></div>
        {close.notes && <p className="muted">{close.notes}</p>}
        {can("cashbook:reopen") && <button className="secondary" onClick={reopen} data-testid="reopen-day-button"><Unlock size={15} /> Reopen day</button>}
      </section>
    );
  }
  if (!can("cashbook:close")) return <section className="close-card"><p className="muted" data-testid="close-not-allowed">An admin or accountant closes the day after counting the drawer.</p></section>;
  const diff = counted === "" ? null : Math.round((Number(counted) - data.expected) * 100) / 100;
  const submit = (e) => { e.preventDefault(); run(async () => { await api.post("/cashbook/close", { date: data.day, counted: Number(counted), notes }); toast.success("Day closed and locked"); onDone(); }); };
  return (
    <form className="close-card" onSubmit={submit} data-testid="close-day-form">
      <p className="eyebrow">CLOSE THE DAY</p>
      <h3>Count the drawer</h3>
      {data.close?.status === "reopened" && <p className="muted">Reopened by {data.close.reopened_by_name}: {data.close.reopen_reason}</p>}
      <label>Cash counted in drawer (₹)<input type="number" min="0" step="0.01" required value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="counted-cash-input" /></label>
      {diff !== null && <div className={diff === 0 ? "diff ok" : "diff off"} data-testid="close-difference-preview">{diff === 0 ? "Matches the expected cash" : `${diff > 0 ? "Excess" : "Short"} by ${inr(Math.abs(diff))}`}</div>}
      <label>Notes{diff ? " (explain the difference)" : ""}<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="close-notes-input" required={!!diff} /></label>
      <ErrorBox error={error} testid="close-day-error" />
      <button className="primary full" disabled={busy} data-testid="close-day-button"><Lock size={16} /> {busy ? "Closing…" : "Close & lock day"}</button>
    </form>
  );
}

function EntryRow({ e, locked, onChanged }) {
  const can = useCan();
  const voidEntry = async () => {
    const reason = window.prompt("Reason for voiding this cash entry:");
    if (!reason) return;
    try { await api.post(`/cashbook/entries/${e.id}/void`, { reason }); toast.success("Entry voided"); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  const In = e.direction === "in";
  return (
    <div className={`cash-row ${e.status === "void" ? "voided" : ""}`} data-testid={`cash-row-${e.id}`}>
      <span className={In ? "cash-dir in" : "cash-dir out"}>{In ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}</span>
      <div className="cash-label">{e.kind === "patient" ? <Link to={`/patients/${e.patient_id}`}>{e.label}</Link> : <b>{e.label}</b>}<small>{e.detail}{e.by && ` · ${e.by}`}{e.void_reason && ` · void: ${e.void_reason}`}</small></div>
      <span className={In ? "amount credit" : "amount due"} data-testid={`cash-amount-${e.id}`}>{In ? "+" : "−"}{inr(e.amount)}</span>
      {e.kind === "patient" ? <button className="icon-btn" title="Print receipt" onClick={() => printReceipt(e.id)} data-testid={`cash-receipt-${e.id}`}><Printer size={15} /></button>
        : can("payments:void") && e.status === "active" && !locked ? <button className="icon-btn" title="Void" onClick={voidEntry} data-testid={`cash-void-${e.id}`}><Ban size={15} /></button> : <span />}
    </div>
  );
}

export default function Cashbook() {
  const can = useCan();
  const [day, setDay] = useState(today());
  const [show, setShow] = useState(false);
  const { data, error, reload } = useLoad(`/cashbook?day=${day}`, null);
  const locked = data?.close?.status === "closed";
  return (
    <div className="content">
      <PageHeading eyebrow="CASHBOOK / DAILY DRAWER" title="Cash in, cash out," accent="matched daily." sub="Cash patient payments and refunds flow in automatically. Add other drawer movements by hand.">
        <input type="date" className="date-input" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="cashbook-day-input" />
        {can("cashbook:write") && !locked && <button className="primary" onClick={() => setShow(true)} data-testid="add-cash-entry-button"><Plus size={18} /> Cash entry</button>}
      </PageHeading>
      <ErrorBox error={error} testid="cashbook-error" />
      {data && <>
        <section className="summary-grid four">
          <div className="mini-stat"><span>Opening cash{data.opening_from ? ` (from ${data.opening_from} close)` : ""}</span><b data-testid="cash-opening">{inr(data.opening)}</b></div>
          <div className="mini-stat"><span>Cash in</span><b className="in" data-testid="cash-in-total">{inr(data.cash_in)}</b></div>
          <div className="mini-stat"><span>Cash out</span><b className="out" data-testid="cash-out-total">{inr(data.cash_out)}</b></div>
          <div className="mini-stat strong"><span>Expected in drawer</span><b data-testid="cash-expected">{inr(data.expected)}</b></div>
        </section>
        <div className="cash-layout">
          <section className="table-card cash-list">
            {data.entries.length ? data.entries.map((e) => <EntryRow key={e.id} e={e} locked={locked} onChanged={reload} />) : <Empty icon={WalletCards} title="No cash movement" text="Cash payments, refunds and manual entries for this day appear here." testid="cashbook-empty" />}
          </section>
          <CloseDay key={`${data.day}-${data.close?.status}-${data.close?.reopened_at || ""}`} data={data} onDone={reload} />
        </div>
      </>}
      {show && <CashEntryForm day={day} onClose={() => setShow(false)} onSaved={() => { setShow(false); reload(); }} />}
    </div>
  );
}
